import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  getUsers,
  deleteUser,
  activateUser,
  deactivateUser,
  updateUser,
  createUser,
} from "../features/users/services/userService.js";

const BORDER_RADIUS = "8px";
const PRIMARY_BG = "#fff";
const SECONDARY_BG = "#f3f4f6";
const ACCENT_BG = "#2563eb";
const ACCENT_TEXT = "#2563eb";
const GRAY_TEXT = "#6b7280";
const DARK_TEXT = "#111827";
const BOX_SHADOW = "0 4px 24px rgba(0,0,0,0.08), 0 1.5px 4px rgba(0,0,0,0.04)";

const PageContainer = styled.div`
  padding: 2rem 1rem;
`;
const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${DARK_TEXT};
  margin-bottom: 1.5rem;
`;
const ActionButton = styled.button`
  background: ${ACCENT_BG};
  color: #fff;
  border: none;
  border-radius: ${BORDER_RADIUS};
  padding: 0.5rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-right: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #1d4ed8;
  }
  &:disabled {
    background: #cbd5e1;
    color: #64748b;
    cursor: not-allowed;
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${PRIMARY_BG};
  border-radius: ${BORDER_RADIUS};
  box-shadow: ${BOX_SHADOW};
  overflow: hidden;
`;
const Th = styled.th`
  background: ${SECONDARY_BG};
  color: ${DARK_TEXT};
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
`;
const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: ${DARK_TEXT};
`;
const Tr = styled.tr`
  &:last-child ${Td} {
    border-bottom: none;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalContent = styled.div`
  background: ${PRIMARY_BG};
  padding: 2rem 2rem 1.5rem 2rem;
  border-radius: ${BORDER_RADIUS};
  min-width: 340px;
  max-width: 95vw;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${BOX_SHADOW};
`;
const ModalTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${DARK_TEXT};
  margin-bottom: 1.25rem;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const FormGroup = styled.div`
  margin-bottom: 1rem;
`;
const Label = styled.label`
  font-size: 1rem;
  font-weight: 500;
  color: ${DARK_TEXT};
  margin-bottom: 0.25rem;
  display: block;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: ${BORDER_RADIUS};
  font-size: 1rem;
  margin-top: 0.25rem;
  background: ${SECONDARY_BG};
  color: ${DARK_TEXT};
  &:focus {
    outline: none;
    border-color: ${ACCENT_BG};
    background: #fff;
  }
`;
const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: ${BORDER_RADIUS};
  font-size: 1rem;
  margin-top: 0.25rem;
  background: ${SECONDARY_BG};
  color: ${DARK_TEXT};
  &:focus {
    outline: none;
    border-color: ${ACCENT_BG};
    background: #fff;
  }
`;
const PermissionGroup = styled.div`
  margin-bottom: 0.75rem;
`;
const PermissionGroupTitle = styled.strong`
  font-size: 1rem;
  color: ${DARK_TEXT};
  display: block;
  margin-bottom: 0.25rem;
`;
const PermissionCheckboxLabel = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.97rem;
  color: ${DARK_TEXT};
  font-weight: 400;
`;
const PermissionCheckbox = styled.input`
  margin-right: 0.5rem;
`;
const SelectAllLabel = styled.label`
  font-weight: 600;
  color: ${ACCENT_TEXT};
  margin-bottom: 0.25rem;
  display: inline-block;
`;
const ErrorMsg = styled.div`
  color: #dc2626;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;
const SuccessMsg = styled.div`
  color: #16a34a;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const PERMISSION_GROUPS = [
  {
    label: "Conversations",
    permissions: [
      "conversations:view",
      "conversations:create",
      "conversations:update",
      "conversations:delete",
      "conversations:assign",
      "conversations:close",
      "conversations:reopen",
      "conversations:transfer",
      "conversations:archive",
      "conversations:export",
    ],
  },
  {
    label: "Messages",
    permissions: [
      "messages:view",
      "messages:send",
      "messages:edit",
      "messages:delete",
      "messages:approve",
      "messages:schedule",
      "messages:templates",
    ],
  },
  {
    label: "Leads",
    permissions: [
      "leads:view",
      "leads:create",
      "leads:update",
      "leads:delete",
      "leads:assign",
      "leads:qualify",
      "leads:convert",
      "leads:import",
      "leads:export",
      "leads:analytics",
    ],
  },
  {
    label: "Quotes",
    permissions: [
      "quotes:view",
      "quotes:create",
      "quotes:update",
      "quotes:delete",
      "quotes:approve",
      "quotes:reject",
      "quotes:send",
      "quotes:templates",
      "quotes:pricing",
      "quotes:analytics",
    ],
  },
  {
    label: "Orders",
    permissions: [
      "orders:view",
      "orders:create",
      "orders:update",
      "orders:delete",
      "orders:approve",
      "orders:cancel",
      "orders:ship",
      "orders:track",
      "orders:invoice",
      "orders:refund",
    ],
  },
  {
    label: "Call Requests",
    permissions: ["call:view", "call:create", "call:manage"],
  },
  {
    label: "Customers",
    permissions: [
      "customers:view",
      "customers:create",
      "customers:update",
      "customers:delete",
      "customers:export",
      "customers:analytics",
    ],
  },
  {
    label: "Users",
    permissions: [
      "users:view",
      "users:create",
      "users:update",
      "users:delete",
      "users:activate",
      "users:deactivate",
      "users:reset_password",
      "users:roles",
      "users:permissions",
      "users:audit",
    ],
  },
  {
    label: "Agents",
    permissions: [
      "agents:view",
      "agents:create",
      "agents:update",
      "agents:delete",
      "agents:assign",
      "agents:performance",
      "agents:schedule",
      "agents:training",
      "agents:evaluation",
    ],
  },
  {
    label: "Reports",
    permissions: [
      "reports:view",
      "reports:create",
      "reports:update",
      "reports:delete",
      "reports:export",
      "reports:schedule",
      "reports:dashboard",
      "reports:analytics",
    ],
  },
  {
    label: "Feedback",
    permissions: [
      "feedback:view",
      "feedback:create",
      "feedback:update",
      "feedback:delete",
      "feedback:respond",
      "feedback:analytics",
    ],
  },
  {
    label: "Follow-ups",
    permissions: [
      "followups:view",
      "followups:create",
      "followups:update",
      "followups:delete",
    ],
  },
  {
    label: "Notifications",
    permissions: [
      "notifications:view",
      "notifications:create",
      "notifications:update",
      "notifications:delete",
      "notifications:send",
      "notifications:templates",
    ],
  },
  {
    label: "Fleet",
    permissions: [
      "fleet:view",
      "fleet:create",
      "fleet:update",
      "fleet:delete",
      "fleet:assign",
      "fleet:maintenance",
      "fleet:tracking",
      "fleet:analytics",
    ],
  },
  {
    label: "System",
    permissions: [
      "system:settings",
      "system:backup",
      "system:restore",
      "system:logs",
      "system:monitoring",
      "system:security",
      "system:integrations",
    ],
  },
  {
    label: "Security",
    permissions: [
      "security:view",
      "security:manage",
      "security:analytics",
      "security:events",
      "security:alerts",
    ],
  },
  {
    label: "Payouts",
    permissions: [
      "payouts:view",
      "payouts:execute",
    ],
  },
  {
    label: "Finance",
    permissions: [
      "finance:audit",
      "finance:reconcile",
    ],
  },
];

const ALL_KNOWN_PERMISSIONS = PERMISSION_GROUPS.flatMap(
  (group) => group.permissions
);

const sanitizeEditPermissions = (permissions) =>
  (permissions || []).filter((p) => ALL_KNOWN_PERMISSIONS.includes(p));

const ROLES = [
  { value: "agent", label: "Agent" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editPermissions, setEditPermissions] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "agent",
    permissions: [],
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  // Create a stable array of refs, one per group
  const selectAllRefs = useRef([]);
  if (selectAllRefs.current.length !== PERMISSION_GROUPS.length) {
    selectAllRefs.current = Array(PERMISSION_GROUPS.length)
      .fill()
      .map(() => React.createRef());
  }

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsers();
      setUsers(res.users || res.data?.users || []);
    } catch (err) {
      setError(err.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openEditModal = (user) => {
    setEditUser(user);
    setEditPermissions(sanitizeEditPermissions(user.permissions));
    setModalMessage(null);
    setModalError(null);
  };

  const closeEditModal = () => {
    setEditUser(null);
    setEditPermissions([]);
    setModalMessage(null);
    setModalError(null);
    setSaving(false);
  };

  const handlePermissionChange = (perm) => {
    setEditPermissions((perms) =>
      perms.includes(perm) ? perms.filter((p) => p !== perm) : [...perms, perm]
    );
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    setModalMessage(null);
    setModalError(null);
    setPasswordError(null);
    // Password validation if changing
    if (showPasswordFields) {
      if (newPassword.length < 8) {
        setPasswordError("Password must be at least 8 characters.");
        setSaving(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match.");
        setSaving(false);
        return;
      }
    }
    try {
      const cleanedPermissions = sanitizeEditPermissions(editPermissions);
      const updatePayload = { permissions: cleanedPermissions };
      if (showPasswordFields && newPassword) {
        updatePayload.password = newPassword;
      }
      await updateUser(editUser.id, updatePayload);
      setModalMessage("User updated!");
      await fetchUsers();
      setTimeout(closeEditModal, 1000);
    } catch (err) {
      setModalError(err.message || "Failed to update user");
    } finally {
      setSaving(false);
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordFields(false);
    }
  };

  const handleNewUserChange = (field, value) => {
    setNewUser((u) => ({ ...u, [field]: value }));
  };
  const handleNewUserPermission = (perm) => {
    setNewUser((u) => ({
      ...u,
      permissions: u.permissions.includes(perm)
        ? u.permissions.filter((p) => p !== perm)
        : [...u.permissions, perm],
    }));
  };
  const handleNewUserGroupSelectAll = (group, checked) => {
    setNewUser((u) => {
      const groupPerms = group.permissions;
      let perms = u.permissions.slice();
      if (checked) {
        perms = Array.from(new Set([...perms, ...groupPerms]));
      } else {
        perms = perms.filter((p) => !groupPerms.includes(p));
      }
      return { ...u, permissions: perms };
    });
  };
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      await createUser(newUser);
      setCreateSuccess("User created!");
      setShowCreateModal(false);
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "agent",
        permissions: [],
      });
      await fetchUsers();
    } catch (err) {
      setCreateError(err.message || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  // Set indeterminate state for all select-all checkboxes after every render
  useEffect(() => {
    PERMISSION_GROUPS.forEach((group, groupIdx) => {
      const allSelected = group.permissions.every((perm) =>
        editPermissions.includes(perm)
      );
      const someSelected = group.permissions.some((perm) =>
        editPermissions.includes(perm)
      );
      const ref = selectAllRefs.current[groupIdx]?.current;
      if (ref) {
        ref.indeterminate = someSelected && !allSelected;
      }
    });
  }, [editPermissions]);

  if (loading) return <PageContainer>Loading users...</PageContainer>;
  if (error)
    return (
      <PageContainer>
        <ErrorMsg>Error: {error}</ErrorMsg>
      </PageContainer>
    );

  return (
    <PageContainer>
      <Title>User Management</Title>
      <ActionButton onClick={() => setShowCreateModal(true)}>
        Add New User
      </ActionButton>
      <Table>
        <thead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <Tr key={user.id}>
              <Td>
                {user.firstName} {user.lastName}
              </Td>
              <Td>{user.email}</Td>
              <Td>{user.role}</Td>
              <Td>{user.status}</Td>
              <Td>
                <ActionButton type="button" onClick={() => openEditModal(user)}>
                  Edit
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={async () => {
                    await activateUser(user.id);
                    fetchUsers();
                  }}
                  disabled={user.status === "active"}
                >
                  Activate
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={async () => {
                    await deactivateUser(user.id);
                    fetchUsers();
                  }}
                  disabled={user.status === "inactive"}
                >
                  Deactivate
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={async () => {
                    await deleteUser(user.id);
                    fetchUsers();
                  }}
                >
                  Delete
                </ActionButton>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {/* Permissions Edit Modal */}
      {editUser && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>
              Edit Permissions for {editUser.firstName} {editUser.lastName}
            </ModalTitle>
            <div
              style={{ maxHeight: 400, overflowY: "auto", marginBottom: 16 }}
            >
              {PERMISSION_GROUPS.map((group, groupIdx) => {
                const allSelected = group.permissions.every((perm) =>
                  editPermissions.includes(perm)
                );
                const handleSelectAll = (e) => {
                  if (e.target.checked) {
                    setEditPermissions((perms) =>
                      Array.from(new Set([...perms, ...group.permissions]))
                    );
                  } else {
                    setEditPermissions((perms) =>
                      perms.filter((perm) => !group.permissions.includes(perm))
                    );
                  }
                };
                return (
                  <PermissionGroup key={group.label}>
                    <PermissionGroupTitle>{group.label}</PermissionGroupTitle>
                    <div style={{ marginLeft: 12, marginBottom: 4 }}>
                      <SelectAllLabel>
                        <PermissionCheckbox
                          type="checkbox"
                          ref={selectAllRefs.current[groupIdx]}
                          checked={allSelected}
                          onChange={handleSelectAll}
                        />
                        Select All
                      </SelectAllLabel>
                    </div>
                    <div style={{ marginLeft: 12 }}>
                      {group.permissions.map((perm) => (
                        <PermissionCheckboxLabel key={perm}>
                          <PermissionCheckbox
                            type="checkbox"
                            checked={editPermissions.includes(perm)}
                            onChange={() => handlePermissionChange(perm)}
                          />
                          {perm}
                        </PermissionCheckboxLabel>
                      ))}
                    </div>
                  </PermissionGroup>
                );
              })}
            </div>
            <div style={{ marginBottom: 16 }}>
              {!showPasswordFields && (
                <ActionButton
                  type="button"
                  onClick={() => setShowPasswordFields(true)}
                  style={{ marginBottom: 8 }}
                >
                  Change Password
                </ActionButton>
              )}
              {showPasswordFields && (
                <FormGroup>
                  <Label>New Password:</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <Label>Confirm Password:</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  {passwordError && <ErrorMsg>{passwordError}</ErrorMsg>}
                </FormGroup>
              )}
            </div>
            {modalMessage && <SuccessMsg>{modalMessage}</SuccessMsg>}
            {modalError && <ErrorMsg>{modalError}</ErrorMsg>}
            <ActionButton
              onClick={handleSavePermissions}
              disabled={saving}
              style={{ marginRight: 8 }}
            >
              Save
            </ActionButton>
            <ActionButton onClick={closeEditModal} disabled={saving}>
              Cancel
            </ActionButton>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Add New User</ModalTitle>
            <Form onSubmit={handleCreateUser}>
              <FormGroup>
                <Label>First Name:</Label>
                <Input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) =>
                    handleNewUserChange("firstName", e.target.value)
                  }
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name:</Label>
                <Input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) =>
                    handleNewUserChange("lastName", e.target.value)
                  }
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email:</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => handleNewUserChange("email", e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Password:</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    handleNewUserChange("password", e.target.value)
                  }
                  required
                  minLength={8}
                />
              </FormGroup>
              <FormGroup>
                <Label>Role:</Label>
                <Select
                  value={newUser.role}
                  onChange={(e) => handleNewUserChange("role", e.target.value)}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <strong>Permissions:</strong>
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    border: "1px solid #eee",
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  {PERMISSION_GROUPS.map((group, groupIdx) => {
                    const allSelected = group.permissions.every((perm) =>
                      newUser.permissions.includes(perm)
                    );
                    const someSelected = group.permissions.some((perm) =>
                      newUser.permissions.includes(perm)
                    );
                    return (
                      <PermissionGroup key={group.label}>
                        <PermissionGroupTitle>
                          {group.label}
                        </PermissionGroupTitle>
                        <div style={{ marginLeft: 12, marginBottom: 4 }}>
                          <SelectAllLabel>
                            <PermissionCheckbox
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el)
                                  el.indeterminate =
                                    someSelected && !allSelected;
                              }}
                              onChange={(e) =>
                                handleNewUserGroupSelectAll(
                                  group,
                                  e.target.checked
                                )
                              }
                            />
                            Select All
                          </SelectAllLabel>
                        </div>
                        <div style={{ marginLeft: 12 }}>
                          {group.permissions.map((perm) => (
                            <PermissionCheckboxLabel key={perm}>
                              <PermissionCheckbox
                                type="checkbox"
                                checked={newUser.permissions.includes(perm)}
                                onChange={() => handleNewUserPermission(perm)}
                              />
                              {perm}
                            </PermissionCheckboxLabel>
                          ))}
                        </div>
                      </PermissionGroup>
                    );
                  })}
                </div>
              </FormGroup>
              {createError && <ErrorMsg>{createError}</ErrorMsg>}
              {createSuccess && <SuccessMsg>{createSuccess}</SuccessMsg>}
              <ActionButton
                type="submit"
                disabled={createLoading}
                style={{ marginRight: 8 }}
              >
                Create
              </ActionButton>
              <ActionButton
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
              >
                Cancel
              </ActionButton>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default UsersPage;
